import { useEffect, useRef, useState } from 'react';
import { Spreadsheet, SpreadsheetHandle, SpreadsheetExcelImportEvent, SpreadsheetExcelExportEvent } from '@progress/kendo-react-spreadsheet';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { Loader } from '@progress/kendo-react-indicators';
import { arrowRotateCwIcon, pencilIcon, saveIcon, unlockIcon, xIcon } from '@progress/kendo-svg-icons';
import { useLocalization } from '@progress/kendo-react-intl';
import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import LoadingSpinner from '../../../Shared/Components/LoadingSpinner';
import DashboardPanel from '../../../Shared/Components/DashboardPanel';
import siteWorkbookApiService, { WorkbookJson } from '../Utilities/SiteWorkbookApiService';
import { enMessages } from '../../../../messages/en-US';
import useIdentityStore from '../../../../Zustand/identityStore';
import moment from 'moment';

// Backend rejects oversized workbooks with 413 Payload Too Large (MongoDB's 16 MB BSON
// document limit, with a safety margin). Keep this aligned with the server-side cap.
const MAX_WORKBOOK_SIZE_MB = 15;
const ONE_MB = 1024 * 1024;

/**
 * Site-wide workbook surface. One Kendo Spreadsheet per site, persisted as opaque JSON.
 * UX is explicit edit mode: workbook opens read-only, user clicks Edit to unlock cells,
 * then either Save (persist + release) or Cancel (discard + release).
 */
export default function SiteWorkbookTab() {
  const localizationService = useLocalization();
  const ref = useRef<SpreadsheetHandle>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [showSavedFor, setShowSavedFor] = useState(false);
  // True once the workbook JSON has been fed into the Spreadsheet via fromJSON — until then
  // (and during a refetch) we cover the grid with a spinner so the user never sees an empty sheet.
  const [workbookReady, setWorkbookReady] = useState(false);
  // True while an .xlsx import is being parsed/loaded into the sheet — covers the grid with a spinner.
  const [isImporting, setIsImporting] = useState(false);
  // Workbook opens read-only by default — user has to explicitly enter edit mode.
  // Lock ownership will eventually come from the backend.
  const [iHaveTheLock, setIHaveTheLock] = useState(false);
  // Tracks whether we've already done the initial fromJSON so we know whether to
  // re-feed the spreadsheet from the query data or from its current saveJSON (preserves edits on lock toggle).
  const initialLoadDoneRef = useRef(false);
  // When set, the next applyLock run skips its re-feed — used when entering edit mode right after an
  // Excel import so we don't clobber/disable the freshly imported (already editable) content.
  const skipReapplyLockRef = useRef(false);
  // Uncompressed byte size of the last workbook we attempted to save — used to tell the user
  // how far over the limit they are when the backend rejects the save with 413.
  const lastAttemptedBytesRef = useRef(0);

  const saveL = localizationService.toLanguageString('custom.save', enMessages.custom.save);
  const savingL = localizationService.toLanguageString('custom.saving', enMessages.custom.saving);
  const allChangesSavedL = localizationService.toLanguageString(
    'custom.allChangesSaved',
    enMessages.custom.allChangesSaved,
  );
  const failedToSaveWorkbookL = localizationService.toLanguageString(
    'custom.failedToSaveWorkbook',
    enMessages.custom.failedToSaveWorkbook,
  );

  const workbookQuery = useQuery(
    ['siteWorkbook'],
    () => siteWorkbookApiService.getSiteWorkbook(),
    {
      staleTime: Infinity,
      refetchOnMount: false,
    },
  );

  const saveMutation = useMutation(
    (content: WorkbookJson) =>
      siteWorkbookApiService.saveSiteWorkbook(content, workbookQuery.data?.name ?? 'Site Workbook'),
    {
      onSuccess: () => {
        setIsDirty(false);
        setShowSavedFor(true);
        window.setTimeout(() => setShowSavedFor(false), 2000);
        // Save = commit + release lock — back to read-only.
        setIHaveTheLock(false);
        // Refresh the server metadata (Last updated, lock owner) — the save released the lock
        // and bumped the timestamp. Content stays as just-saved (effect re-feeds from saveJSON).
        void workbookQuery.refetch();
      },
    },
  );

  // Current user GUID (JWT `sub`) — compared against the workbook lock owner.
  const currentUserId = useIdentityStore((x) => x.user?.profile?.sub) || '';
  // Force-unlock is restricted to elevated roles (mirrors the server-side restriction).
  const currentUserRole = useIdentityStore((x) => x.user?.profile?.role);
  const canForceUnlock = ['SuperAdmin', 'SiteAdmin', 'Manager'].includes(currentUserRole ?? '');
  // Shown when the user clicks Edit but someone else currently holds the lock.
  const [showLockWarning, setShowLockWarning] = useState(false);
  // Set after a force-unlock so the lock check is skipped locally (real backend clears it on refetch).
  const [lockForciblyReleased, setLockForciblyReleased] = useState(false);
  // Confirmation dialog before forcing an unlock — the current holder may lose unsaved changes.
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  // Lock metadata coming from the backend.
  const lock = workbookQuery.data?.lock;
  const lockedByOther = !!lock?.userId && lock.userId !== currentUserId;
  const lockOwnerName = lock?.userName || 'Another user';

  // How far beyond the existing data we pre-populate disabled cells when locked.
  // Covers the typical visible/scrollable area so users can't add content to blank cells.
  const LOCK_ROW_LIMIT = 100;
  const LOCK_COL_LIMIT = 52; // A–Z

  /**
   * Walks every cell in the workbook JSON and toggles their `enable` flag.
   * When `locked`, also pre-populates empty cells up to (rowLimit × colLimit) with `enable: false`
   * so users can't type into blank cells either.
   */
  const applyLockToCells = (workbook: unknown, locked: boolean): unknown => {
    const cloned = JSON.parse(JSON.stringify(workbook));
    // Workbook-level image registry (id -> dataURL). Drawings reference these by id.
    const images = cloned.images && typeof cloned.images === 'object' ? cloned.images : {};
    cloned.sheets?.forEach((sheet: any) => {
      // Kendo's renderer calls imageUrl(image) per drawing and throws on `undefined.url`
      // when a drawing points to an image missing from `images`. Drop those orphans.
      if (Array.isArray(sheet.drawings)) {
        sheet.drawings = sheet.drawings.filter((d: any) => d?.image != null && images[d.image] != null);
      }
      if (!locked) {
        // Unlocking: clear the enable flag everywhere so cells return to normal.
        sheet.rows?.forEach((row: any) => {
          row.cells?.forEach((cell: any) => {
            if (cell.enable === false) delete cell.enable;
          });
        });
        return;
      }

      // Locking: ensure every cell up to the limits exists and is disabled.
      sheet.rows = sheet.rows ?? [];
      const rowByIndex = new Map<number, any>();
      sheet.rows.forEach((row: any) => {
        if (typeof row.index === 'number') rowByIndex.set(row.index, row);
      });

      const maxRowIdx = Math.max(
        LOCK_ROW_LIMIT,
        ...sheet.rows.map((r: any) => r.index ?? 0),
      );
      const maxColIdx = Math.max(
        LOCK_COL_LIMIT,
        ...sheet.rows.flatMap((r: any) =>
          (r.cells ?? []).map((c: any) => c.index ?? 0),
        ),
      );

      for (let r = 0; r <= maxRowIdx; r++) {
        let row = rowByIndex.get(r);
        if (!row) {
          row = { index: r, cells: [] };
          sheet.rows.push(row);
          rowByIndex.set(r, row);
        }
        row.cells = row.cells ?? [];
        const cellByCol = new Map<number, any>();
        row.cells.forEach((cell: any) => {
          if (typeof cell.index === 'number') cellByCol.set(cell.index, cell);
        });
        for (let c = 0; c <= maxColIdx; c++) {
          const existing = cellByCol.get(c);
          if (existing) {
            existing.enable = false;
          } else {
            row.cells.push({ index: c, enable: false });
          }
        }
      }
    });
    return cloned;
  };

  // Re-applies the workbook on initial load and whenever lock state changes.
  // On the very first load we use the fetched content; on subsequent runs we read the
  // current state via saveJSON() (async, preserves embedded images — toJSON breaks them).
  useEffect(() => {
    if (!ref.current?.instance) return;
    const instance = ref.current.instance;

    const applyLock = async () => {
      // Skip the re-feed once (e.g. right after an Excel import) so we don't disable/clobber
      // the freshly imported content — it's already editable as imported.
      if (skipReapplyLockRef.current) {
        skipReapplyLockRef.current = false;
        initialLoadDoneRef.current = true;
        setWorkbookReady(true);
        return;
      }
      const baseJson = initialLoadDoneRef.current
        ? await instance.saveJSON()
        : workbookQuery.data?.data;
      if (!baseJson) return;

      const processed = applyLockToCells(baseJson, !iHaveTheLock);
      instance.fromJSON(processed as never);
      initialLoadDoneRef.current = true;
      setWorkbookReady(true);
    };

    void applyLock();
  }, [workbookQuery.data, iHaveTheLock]);

  const flushSave = async () => {
    const json = await ref.current?.instance?.saveJSON();
    if (!json) return;
    // Approximate the uncompressed payload size so we can report it if the save is rejected as too large.
    lastAttemptedBytesRef.current = new Blob([JSON.stringify(json)]).size;
    saveMutation.mutate(json as unknown as WorkbookJson);
  };
  const handleChange = () => {
    setIsDirty(true);
    setShowSavedFor(false);
  };

  const handleManualSave = () => {
    flushSave();
  };

  const handleEdit = async () => {
    // Re-fetch on every Edit click so the lock check uses the latest server state
    // (someone may have grabbed/released the lock since the last load).
    const { data } = await workbookQuery.refetch();
    const freshLock = data?.lock;
    if (!lockForciblyReleased && freshLock?.userId && freshLock.userId !== currentUserId) {
      setShowLockWarning(true);
      return;
    }
    try {
      await siteWorkbookApiService.acquireLock();
    } catch {
      // 409 Conflict — someone grabbed the lock between the refetch and the acquire.
      setShowLockWarning(true);
      return;
    }
    setShowLockWarning(false);
    // Re-feed from the freshly-refetched server content (not the stale local copy) so you
    // edit the latest version — someone may have saved changes since this tab loaded.
    initialLoadDoneRef.current = false;
    setIHaveTheLock(true);
  };

  // Kendo's Open toolbar button (import an .xlsx) is an edit action.
  // - Already editing → let the import through.
  // - Locked by someone else → block the import so no editable content lands; cells stay disabled
  //   and the lock warning is shown.
  // - Free → auto-enter edit mode (like clicking Edit) and let the import proceed into an editable sheet.
  const handleExcelImport = async (event: SpreadsheetExcelImportEvent) => {
    // Locked by someone else → block the import; cells stay disabled and the warning shows.
    // (preventDefault must run synchronously, before any await, for Kendo to actually cancel.)
    if (!iHaveTheLock && lockedByOther && !lockForciblyReleased) {
      event.preventDefault();
      setShowLockWarning(true);
      return;
    }
    // Let Kendo run its native import (don't preventDefault — that cancels it, and calling fromFile
    // ourselves re-fires this event → infinite recursion). The event carries a `deferred` promise that
    // resolves once parsing finishes — we hang the spinner on it to show progress.
    setShowSavedFor(false);
    setIsImporting(true);
    const importDone = (event as unknown as { deferred?: { promise?: Promise<unknown> } }).deferred?.promise;
    void Promise.resolve(importDone).finally(() => {
      setIsImporting(false);
      setIsDirty(true);
    });

    // Free (not editing yet): enter edit mode and claim the lock, same as clicking Edit.
    if (!iHaveTheLock) {
      // Skip the next lock re-apply so the effect doesn't re-feed and disable the freshly imported content.
      skipReapplyLockRef.current = true;
      setShowLockWarning(false);
      setIHaveTheLock(true);
      // Claim the lock so the later Save isn't rejected (423) — same as clicking Edit.
      try {
        await siteWorkbookApiService.acquireLock();
      } catch {
        // 409 — someone grabbed the lock in the meantime. Warn; Save stays blocked until they retry via Edit.
        setShowLockWarning(true);
      }
    }
  };

  // Strip our read-only cell lock (`enable: false`) from the exported workbook so the .xlsx
  // isn't protected/locked. Kendo's own saveAsExcel tries to do this when not applying protection,
  // but its loop is buggy (`if (rows.cells)` is never truthy), so the flag survives — we redo it here.
  const handleExcelExport = (event: SpreadsheetExcelExportEvent) => {
    (event.workbook as any)?.sheets?.forEach((sheet: any) => {
      sheet.rows?.forEach((row: any) => {
        row.cells?.forEach((cell: any) => {
          delete cell.enable;
        });
      });
    });
  };

  // Force-release someone else's lock (restricted server-side to Manager / SuperAdmin / AccountAdmin).
  const handleForceUnlock = async () => {
    setShowUnlockConfirm(false);
    try {
      await siteWorkbookApiService.forceReleaseLock();
    } catch {
      // Endpoint not deployed in the stub — ignore so the local flow still unlocks for testing.
    }
    setLockForciblyReleased(true);
    setShowLockWarning(false);
    await workbookQuery.refetch();
    // Force-release frees the lock — now claim it under this user so the Save isn't rejected (423).
    try {
      await siteWorkbookApiService.acquireLock();
    } catch {
      // Best effort — if the claim races and fails, the user can retry via Edit.
    }
    // Taking over hands the lock to this user — go straight into edit mode (unlocks the cells)
    // and enable Save right away so they can re-claim/persist the workbook.
    setIHaveTheLock(true);
    setIsDirty(true);
  };

  // Cancel: discard in-memory edits, reload the workbook from the saved server state,
  // and return to read-only. Releases the lock if this user currently holds it.
  const handleCancel = () => {
    // Only release if we actually hold the lock — handleCancel is also called from the
    // "Take over" dialog's Cancel path, where this user never acquired it (avoids a 403).
    if (iHaveTheLock) {
      // Fire-and-forget — the UI returns to read-only regardless; a stale lock clears on next refetch.
      void siteWorkbookApiService.releaseLock().catch(() => {});
    }
    setIsDirty(false);
    setShowLockWarning(false);
    // Force the next applyLock effect to re-feed from workbookQuery.data instead of saveJSON().
    initialLoadDoneRef.current = false;
    setIHaveTheLock(false);
  };

  if (workbookQuery.isLoading) {
    return <LoadingSpinner />;
  }

  const lockWarning = showLockWarning && lockedByOther;
  let statusText = '';
  let statusTone: 'error' | 'warning' | 'muted' = 'muted';
  if (saveMutation.isLoading) {
    statusText = savingL;
  } else if (saveMutation.isError) {
    const saveError = saveMutation.error;
    // 413 Payload Too Large — the workbook exceeds the backend size cap. Tell the user how big it is.
    if (axios.isAxiosError(saveError) && saveError.response?.status === 413) {
      // Prefer the size the backend reports; fall back to the size we measured before sending.
      const serverBytes = (saveError.response?.data as { uncompressedSize?: number })?.uncompressedSize;
      const actualMb = ((serverBytes ?? lastAttemptedBytesRef.current) / ONE_MB).toFixed(1);
      statusText = localizationService
        .toLanguageString('custom.workbookTooLarge', enMessages.custom.workbookTooLarge)
        .replace('{0}', actualMb)
        .replace('{1}', String(MAX_WORKBOOK_SIZE_MB));
    } else {
      statusText = failedToSaveWorkbookL;
    }
    statusTone = 'error';
  } else if (lockWarning) {
    // Lock state is surfaced by the friendly banner below the toolbar, not the status text.
    statusText = '';
  } else if (showSavedFor) {
    statusText = allChangesSavedL;
  } else if (iHaveTheLock && isDirty) {
    statusText = 'Unsaved changes';
    statusTone = 'warning';
  } else if (iHaveTheLock) {
    statusText = 'Editing — Save to publish, or Cancel to discard';
  } else {
    statusText = 'Read only — click Edit to make changes';
  }
  // Errors/locks stay red; every other status shares the amber tone.
  const statusToneClass = statusTone === 'error' ? 'text-red-600' : 'text-amber-600';
  const updatedAt = workbookQuery.data?.updated;

  return (
    <>
      <DashboardPanel
        toolbar={
        <div className="flex items-center gap-2">
          <Tooltip
            key={iHaveTheLock || lockWarning ? 'editing' : 'readonly'}
            anchorElement="target"
            position="bottom"
            parentTitle={true}
          >
          <Button
              themeColor="primary"
              svgIcon={saveIcon}
              onClick={handleManualSave}
              disabled={saveMutation.isLoading || !iHaveTheLock}
              title={saveL}
            />
            {iHaveTheLock || lockWarning ? (
              <Button
                svgIcon={xIcon}
                onClick={handleCancel}
                title="Cancel"
              />
            ) : (
              <Button
                svgIcon={pencilIcon}
                onClick={handleEdit}
                title="Edit"
              />
            )}
          </Tooltip>
          <div className="flex items-center gap-1.5">
            {saveMutation.isLoading && <Loader size="small" type="pulsing" themeColor="primary" />}
            <span className={`font-sans text-xs ${statusToneClass}`} aria-live="polite">
              {statusText}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {updatedAt && (
              <span className="font-sans text-xs text-amber-600 whitespace-nowrap">
                Last updated {moment(updatedAt).fromNow()}
              </span>
            )}
            <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
              <Button
                fillMode="flat"
                svgIcon={arrowRotateCwIcon}
                onClick={() => {
                  // Force the next applyLock effect to re-feed from the fetched server data
                  // (not the stale local saveJSON snapshot) so newly uploaded content shows.
                  initialLoadDoneRef.current = false;
                  void workbookQuery.refetch();
                }}
                disabled={iHaveTheLock || workbookQuery.isFetching}
                title={localizationService.toLanguageString('custom.refresh', enMessages.custom.refresh)}
              />
            </Tooltip>
          </div>
        </div>
        }
      >
        {lockWarning && (
          <div className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5">
            <span className="font-sans text-sm text-gray-700">
              <span className="font-semibold text-gray-900">{lockOwnerName}</span> is editing this
              workbook right now — you'll be able to make changes once they're done.
            </span>
            {canForceUnlock && (
              <Button
                fillMode="flat"
                themeColor="primary"
                svgIcon={unlockIcon}
                onClick={() => setShowUnlockConfirm(true)}
              >
                Take over
              </Button>
            )}
          </div>
        )}
        <div
          className={`relative flex flex-1 min-h-0 ${iHaveTheLock ? '' : 'site-workbook-readonly'}`}
          // Read-only: the sheets bar lets you rename a tab via double-click regardless of cell
          // locking. Cancel it in the capture phase before Kendo's handler enters rename mode.
          onDoubleClickCapture={
            iHaveTheLock
              ? undefined
              : (e) => {
                  if ((e.target as HTMLElement).closest?.('.k-spreadsheet-sheets-bar')) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }
          }
        >
          <Spreadsheet
            ref={ref}
            style={{ width: '100%', height: '100%' }}
            onChange={handleChange}
            onExcelImport={handleExcelImport}
            onExcelExport={handleExcelExport}
          />
          {(!workbookReady || workbookQuery.isFetching || isImporting) && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/70">
              <Loader size="large" type="pulsing" themeColor="primary" />
              {isImporting && (
                <span className="font-sans text-sm text-gray-700">
                  {localizationService.toLanguageString(
                    'custom.importingWorkbook',
                    enMessages.custom.importingWorkbook,
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      </DashboardPanel>

      {showUnlockConfirm && (
        <Dialog
          title="Take over editing?"
          onClose={() => {
            setShowUnlockConfirm(false);
            handleCancel();
          }}
          width={440}
        >
          <p className="py-2 pr-2 font-sans text-sm leading-relaxed text-gray-700">
            <span className="font-semibold text-gray-900">{lockOwnerName}</span> is currently
            editing this workbook. Taking over may cause them to lose unsaved changes.
          </p>
          <DialogActionsBar layout="end">
            <Button
              themeColor="base"
              onClick={() => {
                setShowUnlockConfirm(false);
                handleCancel();
              }}
            >
              Cancel
            </Button>
            <Button themeColor="primary" onClick={handleForceUnlock}>
              Take over
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
}
